import {ComponentStore} from '@ngrx/component-store';
import {
  ReportTransactionIdState, TransactionIdRequestParams
} from './report-transaction-id';
import {EventEmitter, Injectable} from '@angular/core';
import {ReportStore} from './report.store';
import {ReportTransactionIdService} from '../../services/report-transaction-id.service';
import {Observable, Subscription, switchMap, throwError, withLatestFrom} from 'rxjs';
import {AdReportParams} from '../../interfaces/ad-reports';
import {catchError, filter, finalize, first, tap} from 'rxjs/operators';
import {ReportStoreState} from './report';
import {Serializer} from '../../interfaces/serializer';
import {Site} from '../../interfaces/site';
import {AppState} from '../store';
import {Store} from '@ngrx/store';
import {selectSite} from '../init/init.selectors';
import {AgGridEvent, ColDef, IServerSideGetRowsParams} from "@ag-grid-community/core";
import {IServerSideDatasource} from "@ag-grid-enterprise/all-modules";

@Injectable()
export class ReportTransactionIdStore extends ComponentStore<ReportTransactionIdState> {
  private _requestSubscription: Subscription | undefined;
  private readonly _dataSet$: Observable<Serializer<Site>> = this._store.select(selectSite);

  public readonly details$: Observable<any> = this.select((state: ReportTransactionIdState): any => state.details);
  public readonly loading$: Observable<boolean> = this.select((state: ReportTransactionIdState): boolean => state.loading);
  public readonly onChanges$: EventEmitter<AgGridEvent> = new EventEmitter<AgGridEvent>();
  public readonly rowCount$: Observable<number> = this.select((state: ReportTransactionIdState): number => state.rowCount);
  public readonly pageSize$: Observable<number> = this.select((state: ReportTransactionIdState): number => state.pageSize);
  public readonly transactionIds$: Observable<Array<string>> = this.select((state: ReportTransactionIdState): Array<string> => state.transactionIds);

  private readonly _limit: number = 25;

  private readonly _params: (first: boolean) => void = this.effect((first$: Observable<boolean>): Observable<[Partial<AdReportParams>, ReportStoreState, ReportTransactionIdState, Serializer<Site>]> => first$.pipe(
    switchMap((first: boolean): Observable<[Partial<AdReportParams>, ReportStoreState, ReportTransactionIdState, Serializer<Site>]> => this._reportStore.reportParamsSubmitted$.pipe(
      withLatestFrom(this._reportStore.state$, this.state$, this._dataSet$),
      filter(([params, reportStoreState]: [Partial<AdReportParams> | null, ReportStoreState, ReportTransactionIdState, Serializer<Site>]): boolean => reportStoreState.submitted),
      tap(([reportParams, reportStoreState, reportTransactionIdStoreState, dataSet]: [Partial<AdReportParams>, ReportStoreState, ReportTransactionIdState, Serializer<Site>]): void => {
        reportTransactionIdStoreState.agGrid.api.setColumnDefs(reportTransactionIdStoreState.columnDefs);

        const pageSize: number = reportStoreState.limit || reportTransactionIdStoreState.pageSize;

        if (!first) {
          reportTransactionIdStoreState.agGrid.api.setServerSideDatasource(
            this._createDatasource(
              dataSet,
              reportTransactionIdStoreState.agGrid,
              reportStoreState.requestParamsSubmitted,
              pageSize
            )
          );
        }

        this.patchState({
          pageSize: pageSize
        });
      })
    ))
  ));

  private _createDatasource(
    dataSet: Serializer<Site>,
    agGrid: AgGridEvent,
    params: Partial<TransactionIdRequestParams>,
    pageSize: number,
  ): IServerSideDatasource {
    this._requestSubscription?.unsubscribe();

    return {
      getRows: (agGridParams: IServerSideGetRowsParams): void => {
        agGridParams.api.showLoadingOverlay();

        params['order'] = agGridParams.api.getSortModel();

        if (agGridParams.api.getFilterApi('id').getModel() !== null) {
          params['transaction_ids'] = agGridParams.api.getFilterApi('id').getModel()['values'];
        } else {
          params['transaction_ids'] = [];
        }

        this._requestSubscription?.unsubscribe();
        this._requestSubscription = this._reportTransactionIdService.generate(dataSet, params, pageSize, agGrid.api.paginationGetCurrentPage() + 1)
          .pipe(
            first(),
            catchError((err: never): Observable<never> => {
              agGridParams.fail();
              return throwError(err);
            }),
            finalize((): void => {
              agGridParams.api.hideOverlay();
            })
          )
          .subscribe((data): void => {
            this.patchState({
              rowCount: data.total,
              transactionIds: data.transaction_ids
            });

            agGridParams.api.getFilterApi('id').setModel(params['transaction_ids']);

            agGridParams.success({
              rowData:  data.rows,
              rowCount: data.total
            });

            this.onChanges$.emit(agGrid);

            this._requestSubscription?.unsubscribe();
          });
      }
    };
  }

  public readonly details: (row: any | null) => void = this.effect((params$: Observable<any>) => params$.pipe(
    tap((row: any): void => {
      this.patchState({ details: row });
    })
  ));

  public changePaginationPageSize(limit: number): void {
    this.patchState({
      pageSize: limit
    });

    this._reportStore.updateLimit(limit);
    this._params(false);
  }

  constructor(
    private readonly _store: Store<AppState>,
    private readonly _reportStore: ReportStore,
    private readonly _reportTransactionIdService: ReportTransactionIdService
  ) {
    super({
      agGrid: null,
      suppressTotals: false,
      suppressPageTotals: false,
      rowCount: 0,
      pageSize: null,
      details: null,
      loading: false,
      transactionIds: [],
      columnDefs: []
    });
  }

  public init(
    agGrid: AgGridEvent,
    columnDefs: ColDef[],
    suppressTotals: boolean = false,
    suppressPageTotals: boolean = false
  ): void {
    this.patchState({
      agGrid,
      columnDefs,
      suppressTotals,
      suppressPageTotals,
    });

    this._params(true);
    this._reportStore.updateLimit(this._limit);
  }
}
